import { useState, useCallback } from 'react';
import type { LegalBids } from '../../types/problems';

interface BidInputProps {
  onSubmit: (bid: string) => void;
  disabled?: boolean;
  /** Legal bids computed from auction state (optional for backward compatibility) */
  legalBids?: LegalBids;
  /** Callback when level selection changes (for dynamic suit filtering) */
  onLevelChange?: (level: number | null) => void;
  /**
   * When true, level/suit pads render at 44 × 44 (WCAG 2.5.5 AAA hit-area) instead
   * of 40 × 40, and Pass/Dbl/Rdbl gain a small vertical padding bump. Opt-in so
   * Practice Problems (which is space-constrained for other reasons) keeps the
   * tighter sizing. The `short:` (max-height: 750px) variant is unchanged
   * regardless of this flag — landscape phones stay at 32 × 32.
   */
  largerTargets?: boolean;
}

const ALL_LEVELS = [1, 2, 3, 4, 5, 6, 7];
const ALL_SUITS = [
  { value: 'C', symbol: '\u2663', color: 'text-black', name: 'Clubs' },
  { value: 'D', symbol: '\u2666', color: 'text-red-600', name: 'Diamonds' },
  { value: 'H', symbol: '\u2665', color: 'text-red-600', name: 'Hearts' },
  { value: 'S', symbol: '\u2660', color: 'text-black', name: 'Spades' },
  { value: 'NT', symbol: 'NT', color: 'text-ink', name: 'No Trump' },
];

export function BidInput({
  onSubmit,
  disabled = false,
  legalBids,
  onLevelChange,
  largerTargets = false,
}: BidInputProps) {
  // largerTargets: lift level/suit pads to 44x44 (WCAG 2.5.5 AAA) on most viewports.
  // snug-phone-pt: revert to 40x40 on tight portrait phones (height 731-800) where the
  // extra 12px would push the auction below the 3-rows-fully-visible layout requirement.
  const padSize = largerTargets ? 'w-11 h-11 snug-phone-pt:w-10 snug-phone-pt:h-10' : 'w-10 h-10';
  const specialPadY = largerTargets ? 'py-2.5 snug-phone-pt:py-2' : 'py-2';
  const specialPadX = largerTargets ? 'px-5 snug-phone-pt:px-4' : 'px-4';
  const [level, setLevel] = useState<number | null>(null);

  // Determine which levels and suits to show
  // When legalBids is provided, use it; otherwise show all (backward compatibility)
  const availableLevels = legalBids?.levels ?? ALL_LEVELS;
  const availableSuits = legalBids?.suits ?? ALL_SUITS.map(s => s.value);
  const canShowDouble = legalBids?.canDouble ?? true;
  const canShowRedouble = legalBids?.canRedouble ?? true;
  // Pass is always available (FR-014)
  const canShowPass = legalBids?.canPass ?? true;

  const handleLevelClick = useCallback((l: number) => {
    setLevel(l);
    // Notify parent of level change for dynamic suit filtering
    onLevelChange?.(l);
  }, [onLevelChange]);

  // T042: Defense-in-depth validation - prevent illegal bid submission even if button visible
  const handleSuitClick = useCallback((s: string) => {
    if (level === null) return;

    // Defense in depth: Verify bid is legal before submitting
    if (legalBids) {
      const isLegalLevel = legalBids.levels.includes(level);
      const isLegalSuit = legalBids.suits.includes(s);
      if (!isLegalLevel || !isLegalSuit) {
        console.warn(`Blocked illegal bid attempt: ${level}${s}`);
        return;
      }
    }

    onSubmit(`${level}${s}`);
    setLevel(null);
    onLevelChange?.(null); // Reset level selection after submission
  }, [level, legalBids, onSubmit, onLevelChange]);

  // T042: Defense-in-depth validation for special calls
  const handleSpecialCall = useCallback((call: string) => {
    // Defense in depth: Verify special call is legal before submitting
    if (legalBids) {
      if (call === 'X' && !legalBids.canDouble) {
        console.warn('Blocked illegal Double attempt');
        return;
      }
      if (call === 'XX' && !legalBids.canRedouble) {
        console.warn('Blocked illegal Redouble attempt');
        return;
      }
      // Pass is always legal (FR-014), but verify anyway
      if (call === 'Pass' && !legalBids.canPass) {
        console.warn('Blocked illegal Pass attempt (should never happen)');
        return;
      }
    }

    onSubmit(call);
    setLevel(null);
    onLevelChange?.(null); // Reset level selection after submission
  }, [legalBids, onSubmit, onLevelChange]);

  return (
    // Compact layout without section labels (professional bridge players understand the controls)
    // CSS media queries handle responsive sizing via short: variant (< 750px viewport height)
    // Fixed min-width ensures container doesn't shrink when fewer levels/suits are displayed
    // Width calculation: 7 buttons * 2.5rem + 6 gaps * 0.5rem = 20.5rem (short: 7 * 2rem + 6 * 0.25rem = 15.5rem)
    <div className="space-y-2 short:space-y-1" style={{ minWidth: 'clamp(12rem, 30vw, 20.5rem)' }}>
      {/* Level Selection - show only legal levels, hide unavailable ones.
          flex-wrap so the 7 level pads drop to a second line instead of
          overflowing when the column is narrow (tight landscape phones). */}
      <div className="flex flex-wrap gap-2 short:gap-1 snug-phone-pt:gap-1">
        {ALL_LEVELS.map((l) => {
          const isLegalLevel = availableLevels.includes(l);
          // Hide illegal levels entirely (like suits and Dbl/Rdbl)
          if (!isLegalLevel) {
            return null;
          }
          const isSelected = level === l;
          return (
            <button
              key={l}
              type="button"
              onClick={() => handleLevelClick(l)}
              disabled={disabled}
              className={`${padSize} short:w-8 short:h-8 rounded-md text-base short:text-sm font-bold ${
                isSelected
                  ? 'bg-primary-600 text-white'
                  : 'bg-cream text-ink hover:bg-paper'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {l}
            </button>
          );
        })}
      </div>

      {/* Suit Selection - show only legal suits for selected level */}
      <div className="flex flex-wrap gap-2 short:gap-1 snug-phone-pt:gap-1">
        {ALL_SUITS.map((s) => {
          const isLegalSuit = availableSuits.includes(s.value);
          // Only show suits that are legal for the current auction state
          // Hide illegal suits entirely for cleaner UX (per clarification in spec.md)
          if (!isLegalSuit) {
            return null;
          }
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => handleSuitClick(s.value)}
              disabled={disabled || level === null}
              title={s.name}
              className={`${padSize} short:w-8 short:h-8 rounded-md ${s.color} bg-cream hover:bg-paper disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-[1.5rem] leading-none font-suits`}
            >
              {s.value === 'NT' ? <span className="text-base short:text-sm font-sans">NT</span> : s.symbol}
            </button>
          );
        })}
      </div>

      {/* Special Calls - Pass always visible, Double/Redouble conditional */}
      <div className="flex flex-wrap gap-2 short:gap-1 snug-phone-pt:gap-1">
        {/* Pass is always available (FR-014) */}
        {canShowPass && (
          <button
            type="button"
            onClick={() => handleSpecialCall('Pass')}
            disabled={disabled}
            className={`${specialPadX} ${specialPadY} short:px-2 short:py-1 rounded-md font-medium short:text-sm bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Pass
          </button>
        )}
        {/* Double - only when opponent's bid is undoubled (FR-010) */}
        {canShowDouble && (
          <button
            type="button"
            onClick={() => handleSpecialCall('X')}
            disabled={disabled}
            className={`${specialPadX} ${specialPadY} short:px-2 short:py-1 rounded-md font-medium short:text-sm bg-red-100 text-red-800 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Dbl
          </button>
        )}
        {/* Redouble - only when our bid was doubled (FR-011) */}
        {canShowRedouble && (
          <button
            type="button"
            onClick={() => handleSpecialCall('XX')}
            disabled={disabled}
            className={`${specialPadX} ${specialPadY} short:px-2 short:py-1 rounded-md font-medium short:text-sm bg-purple-100 text-purple-800 hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Rdbl
          </button>
        )}
      </div>

    </div>
  );
}
